import * as React from "react";
import Loadable from "react-loadable";
import { Route, Switch } from "react-router-dom";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";

import { LOADABLE_COMPONENT_DELAY } from "@config/consts";
import { ROUTES } from "@config/app.routing";

import { AVAILABLE_COUNTRIES } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { FadeInContainer, Section, OptionPanel } from "@ui/Atoms";
import { Column, Container, Row } from "@ui/Atoms/Grid";
import { StepProgress } from "@ui/Molecules";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";
import {
  FEATURE_TOGGLE_ORIGIN,
  useFeatureToggleManager,
} from "@hooks/useFeatureToggle";

import {
  SidebarSkeleton,
  PageSkeleton,
} from "@app/pages/Booking/Booking.skeletons";
import {
  getQueryParams,
  getPricingLoadingState,
} from "@app/redux/booking/booking.selectors";
import { NotFound } from "@app/pages/NotFound";

import { BookingSummary } from "./Sidebar";
import { BookingModals } from "./Booking.modals";

const LoadableCleaningInformation = Loadable({
  loader: () => import("./BookingProcess/CleaningInformation"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.CleaningInformation;
    return <Component {...props} />;
  },
  loading: PageSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableCheckout = Loadable({
  loader: () => import("./BookingProcess/Checkout"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.Checkout;
    return <Component {...props} />;
  },
  loading: PageSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableSidebar = Loadable({
  loader: () => import("./Booking.sidebar"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.BookingSidebar;
    return <Component {...props} />;
  },
  loading: SidebarSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

interface Props {
  currentLocation: string;
}

const routeToStepMap: { [k: string]: number } = {
  [ROUTES.BOOKING]: 2,
  [ROUTES.BOOKING_CHECKOUT]: 3,
};

const fetchedArrayState = [LOADING_STATE.SUCCEEDED, LOADING_STATE.FAILED];

const BookingViewComponent = React.memo((props: Props) => {
  const translate = useTranslate("batmaid_pwa_booking");
  const { desktop, desktopLarge } = useBreakpoint("v1");
  const { country } = useLocalisation();

  const bookingQueryParams = useSelector(getQueryParams);
  const pricingState = useSelector(getPricingLoadingState);

  const [isSidebarVisible, setIsSidebarVisible] = React.useState(() =>
    fetchedArrayState.includes(pricingState),
  );

  React.useEffect(() => {
    if (isSidebarVisible) return;

    setIsSidebarVisible(fetchedArrayState.includes(pricingState));
  }, [pricingState, isSidebarVisible]);

  const renderSidebar = desktop || desktopLarge;

  const summary = {
    translate: translate,
    isDesktop: renderSidebar,
  };

  const { origin: featureToggleOrigin, status: featureToggleStatus } =
    useFeatureToggleManager();
  const featureTogglesAreReady =
    featureToggleOrigin !== FEATURE_TOGGLE_ORIGIN.DEFAULT ||
    fetchedArrayState.includes(featureToggleStatus);

  return (
    <StyledContainer>
      {country === AVAILABLE_COUNTRIES.CH && (
        <Helmet>
          <title>{translate("meta_title")}</title>
        </Helmet>
      )}
      <BookingModals />
      {!renderSidebar && (
        <BookingSummary isDesktop={summary.isDesktop} withHeader={false} />
      )}
      <Row>
        <Column xs={12} lg={8}>
          <Section flat smallMargins>
            <OptionPanel isThin>
              <StepProgress
                activeStep={routeToStepMap[props.currentLocation]}
                steps={[
                  {
                    label: translate("step_postal_code"),
                  },
                  {
                    label: translate("step_cleaning_information"),
                    route: `${ROUTES.BOOKING}${bookingQueryParams}`,
                    allowDirectNavigation: true,
                  },
                  {
                    label: translate("step_check_out"),
                    route: ROUTES.BOOKING_CHECKOUT,
                  },
                ]}
              />
            </OptionPanel>
          </Section>
          <Section small noBottomMargin>
            <Switch>
              <Route
                exact
                path={ROUTES.BOOKING}
                render={() => (
                  <FadeInContainer>
                    {featureTogglesAreReady && <LoadableCleaningInformation />}
                    {!featureTogglesAreReady && <PageSkeleton />}
                  </FadeInContainer>
                )}
              />
              <Route
                exact
                path={ROUTES.BOOKING_CHECKOUT}
                render={() => (
                  <FadeInContainer>
                    {featureTogglesAreReady && <LoadableCheckout />}
                    {!featureTogglesAreReady && <PageSkeleton />}
                  </FadeInContainer>
                )}
              />
              <Route render={() => <NotFound isNested />} />
            </Switch>
          </Section>
        </Column>
        {renderSidebar && (
          <Column lg={4}>
            {featureTogglesAreReady && isSidebarVisible && (
              <LoadableSidebar {...summary} />
            )}
            {(!featureTogglesAreReady || !isSidebarVisible) && (
              <SidebarSkeleton />
            )}
          </Column>
        )}
      </Row>
    </StyledContainer>
  );
});

export { BookingViewComponent as BookingView };

BookingViewComponent.displayName = "BookingView";

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.upToTablet} {
    padding-left: ${props => props.theme.spacing.spacing_24};
    padding-right: ${props => props.theme.spacing.spacing_24};
    padding-top: ${props => props.theme.spacing.spacing_16};
  }

  ${props => props.theme.breakpoints.desktop} {
    padding-top: ${props => props.theme.margins.base_x4};
    padding-bottom: ${props => props.theme.margins.base_x4};
    display: block;
  }

  > ${Row} {
    ${props => props.theme.breakpoints.upToDesktop} {
      margin-left: 0;
      margin-right: 0;
    }
  }
`;
